import { useState, useEffect } from '@wordpress/element';
import { __, sprintf }         from '@wordpress/i18n';
import { classNames }           from '../../../utils/helpers';
import { isInWishlist, toggleWishlist, onWishlistChange } from '../../../utils/wishlist';

export default function QuickViewModal() {
	const [ product, setProduct ] = useState( null );

	useEffect( () => {
		const handler = ( e ) => setProduct( e.detail );
		document.addEventListener( 'mwsb:quick-view', handler );
		return () => document.removeEventListener( 'mwsb:quick-view', handler );
	}, [] );

	useEffect( () => {
		if ( ! product ) return;
		const handler = ( e ) => { if ( e.key === 'Escape' ) setProduct( null ); };
		document.addEventListener( 'keydown', handler );
		return () => document.removeEventListener( 'keydown', handler );
	}, [ product ] );

	useEffect( () => {
		document.body.style.overflow = product ? 'hidden' : '';
		return () => { document.body.style.overflow = ''; };
	}, [ product ] );

	if ( ! product ) return null;

	return (
		<>
			<div
				className="mwsb-qv-overlay"
				onClick={ () => setProduct( null ) }
				aria-hidden="true"
			/>
			<div
				className="mwsb-qv-modal"
				role="dialog"
				aria-modal="true"
				aria-label={ product.name }
			>
				<button
					className="mwsb-qv-modal__close"
					onClick={ () => setProduct( null ) }
					aria-label={ __( 'Close', 'mwsb' ) }
				>
					&#x2715;
				</button>
				<QuickViewContent product={ product } onClose={ () => setProduct( null ) } />
			</div>
		</>
	);
}

function QuickViewContent( { product, onClose } ) {
	const [ activeImage, setActiveImage ] = useState( 0 );
	const [ adding, setAdding ]           = useState( false );
	const [ added, setAdded ]             = useState( false );
	const [ wishlisted, setWishlisted ]   = useState( () => isInWishlist( product.id ) );

	const images     = product.images || [];
	const hasGallery = images.length > 1;
	const outOfStock = product.stock_status === 'outofstock';

	useEffect( () => {
		return onWishlistChange( ( ids ) => setWishlisted( ids.includes( product.id ) ) );
	}, [ product.id ] );

	function handleWishlist() {
		setWishlisted( toggleWishlist( product.id ) );
	}

	async function handleAddToCart() {
		if ( product.type !== 'simple' ) {
			window.location.href = product.permalink;
			return;
		}
		setAdding( true );
		try {
			const response = await fetch( window.mwsbData?.ajaxUrl, {
				method:  'POST',
				headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
				body:    new URLSearchParams( {
					action:     'woocommerce_add_to_cart',
					product_id: product.id,
					quantity:   1,
					security:   window.mwsbData?.nonce,
				} ),
			} );
			const data = await response.json();

			if ( window.jQuery && data.fragments ) {
				const $ = window.jQuery;
				$.each( data.fragments, function( selector, html ) {
					$( selector ).replaceWith( html );
				} );
				$( document.body ).trigger( 'added_to_cart', [ data.fragments, data.cart_hash ] );
			}

			window.wp?.data
				?.dispatch( 'wc/store/cart' )
				?.invalidateResolutionForStore?.();

			document.body.dispatchEvent(
				new CustomEvent( 'wc-blocks_added_to_cart', { bubbles: true, detail: { productId: product.id } } )
			);

			setAdded( true );
			setTimeout( () => setAdded( false ), 2000 );
		} finally {
			setAdding( false );
		}
	}

	return (
		<div className="mwsb-qv-body">
			{ /* Image column */ }
			<div className="mwsb-qv-gallery">
				{ images.length > 0 ? (
					<>
						<div className="mwsb-qv-gallery__main">
							<img
								src={ images[ activeImage ] }
								alt={ product.name }
								className="mwsb-qv-gallery__img"
							/>
							{ product.on_sale && (
								<span className="mwsb-badge mwsb-badge--sale">{ __( 'Sale', 'mwsb' ) }</span>
							) }
							{ outOfStock && (
								<span className="mwsb-badge mwsb-badge--stock">{ __( 'Out of Stock', 'mwsb' ) }</span>
							) }
						</div>

						{ hasGallery && (
							<div className="mwsb-qv-gallery__thumbs">
								{ images.map( ( src, i ) => (
									<button
										key={ i }
										className={ classNames( 'mwsb-qv-thumb', i === activeImage && 'is-active' ) }
										onClick={ () => setActiveImage( i ) }
										aria-label={ sprintf( __( 'Image %d', 'mwsb' ), i + 1 ) }
									>
										<img src={ src } alt="" width="60" height="60" />
									</button>
								) ) }
							</div>
						) }
					</>
				) : (
					<div className="mwsb-qv-gallery__placeholder" />
				) }
			</div>

			{ /* Details column */ }
			<div className="mwsb-qv-details">
				{ product.categories?.length > 0 && (
					<p className="mwsb-qv-details__cats">
						{ product.categories.map( ( c ) => c.name ).join( ', ' ) }
					</p>
				) }

				<h2 className="mwsb-qv-details__title">
					<a href={ product.permalink } onClick={ onClose }>{ product.name }</a>
				</h2>

				{ product.rating?.count > 0 && (
					<div className="mwsb-qv-details__rating">
						<Stars rating={ product.rating.average } />
						<span className="mwsb-product-card__rating-count">
							({ product.rating.count })
						</span>
					</div>
				) }

				{ /* price_html comes from WooCommerce — safe to inject. */ }
				<div
					className="mwsb-qv-details__price"
					dangerouslySetInnerHTML={ { __html: product.price_html } }
				/>

				{ product.short_description && (
					<p className="mwsb-qv-details__desc">{ product.short_description }</p>
				) }

				{ product.sku && (
					<p className="mwsb-qv-details__sku">{ __( 'SKU:', 'mwsb' ) } <span>{ product.sku }</span></p>
				) }

				<div className="mwsb-qv-details__actions">
					<button
						className={ classNames(
							'mwsb-btn mwsb-btn--cart mwsb-qv-details__cart-btn',
							adding && 'mwsb-btn--loading',
							outOfStock && 'mwsb-btn--disabled',
							added && 'mwsb-btn--added',
						) }
						onClick={ handleAddToCart }
						disabled={ outOfStock || adding }
					>
						{ adding && <span className="mwsb-spinner" /> }
						{ ! adding && added && __( 'Added', 'mwsb' ) }
						{ ! adding && ! added && ( outOfStock ? __( 'Out of Stock', 'mwsb' ) : product.add_to_cart?.text || __( 'Add to cart', 'mwsb' ) ) }
					</button>

					<button
						className={ classNames( 'mwsb-btn mwsb-btn--wishlist', wishlisted && 'mwsb-btn--wishlist-active' ) }
						onClick={ handleWishlist }
						aria-label={ wishlisted ? __( 'Remove from wishlist', 'mwsb' ) : __( 'Add to wishlist', 'mwsb' ) }
						aria-pressed={ wishlisted }
					>
						<HeartIcon filled={ wishlisted } />
					</button>
				</div>

				<a href={ product.permalink } className="mwsb-qv-details__view-link" onClick={ onClose }>
					{ __( 'View full details →', 'mwsb' ) }
				</a>
			</div>
		</div>
	);
}

function Stars( { rating } ) {
	const full  = Math.floor( rating );
	const half  = rating - full >= 0.5 ? 1 : 0;
	const empty = 5 - full - half;

	return (
		<span className="mwsb-stars">
			{ Array.from( { length: full } ).map( ( _, i ) => <Star key={ `f${ i }` } type="full" /> ) }
			{ half === 1 && <Star type="half" /> }
			{ Array.from( { length: empty } ).map( ( _, i ) => <Star key={ `e${ i }` } type="empty" /> ) }
		</span>
	);
}

function Star( { type } ) {
	const fill = type === 'full' ? '#f0c040' : type === 'half' ? 'url(#half-qv)' : '#ddd';
	return (
		<svg className={ `mwsb-star mwsb-star--${ type }` } viewBox="0 0 24 24" width="14" height="14">
			{ type === 'half' && (
				<defs>
					<linearGradient id="half-qv">
						<stop offset="50%" stopColor="#f0c040" />
						<stop offset="50%" stopColor="#ddd" />
					</linearGradient>
				</defs>
			) }
			<path
				d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
				fill={ fill }
			/>
		</svg>
	);
}

function HeartIcon( { filled } ) {
	return (
		<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
			<path
				d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
				fill={ filled ? 'currentColor' : 'none' }
				stroke="currentColor"
				strokeWidth={ filled ? '0' : '2' }
			/>
		</svg>
	);
}
